'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { MapPin, Users, Search, SlidersHorizontal, ArrowRight, CheckCircle, X } from 'lucide-react';
import HomepageNav from '../components/HomepageNav';
import HomepageFooter from '../components/HomepageFooter';

interface Project {
  id: string;
  image: string;
  alt: string;
  name: string;
  location: string;
  country: string;
  city: string;
  type: string;
  totalAmount: number;
  fundedAmount: number;
  investors: number;
  status: string;
  statusColor: string;
}

const allProjects: Project[] = [
{
  id: 'proj-1',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1c69f2eb9-1769319760658.png",
  alt: 'Modern residential apartment complex under construction in Lagos Nigeria',
  name: 'Lekki Phase 2 Residences',
  location: 'Lagos, Nigeria',
  country: 'Nigeria',
  city: 'Lagos',
  type: 'Residential',
  totalAmount: 2500000,
  fundedAmount: 1750000,
  investors: 34,
  status: 'Active',
  statusColor: 'text-secondary bg-secondary/8 border-secondary/20'
},
{
  id: 'proj-2',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1331d1eba-1777192468864.png",
  alt: 'Commercial office tower development in Nairobi Kenya business district',
  name: 'Westlands Commercial Tower',
  location: 'Nairobi, Kenya',
  country: 'Kenya',
  city: 'Nairobi',
  type: 'Commercial',
  totalAmount: 5000000,
  fundedAmount: 2100000,
  investors: 18,
  status: 'Funding',
  statusColor: 'text-primary bg-primary/8 border-primary/20'
},
{
  id: 'proj-3',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1dc95bc5b-1775707266209.png",
  alt: 'Luxury villa development with pool in Accra Ghana residential estate',
  name: 'East Legon Luxury Villas',
  location: 'Accra, Ghana',
  country: 'Ghana',
  city: 'Accra',
  type: 'Luxury',
  totalAmount: 1800000,
  fundedAmount: 1800000,
  investors: 12,
  status: 'Completed',
  statusColor: 'text-muted-foreground bg-muted border-border'
},
{
  id: 'proj-4',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1010382cb-1773214638116.png",
  alt: 'Mixed-use development with retail and apartments in Johannesburg South Africa',
  name: 'Sandton Mixed-Use Development',
  location: 'Johannesburg, South Africa',
  country: 'South Africa',
  city: 'Johannesburg',
  type: 'Mixed-Use',
  totalAmount: 8000000,
  fundedAmount: 3200000,
  investors: 52,
  status: 'Funding',
  statusColor: 'text-primary bg-primary/8 border-primary/20'
},
{
  id: 'proj-5',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1223ee744-1773887362362.png",
  alt: 'Affordable housing estate development in Abuja Nigeria government district',
  name: 'Abuja Affordable Housing Estate',
  location: 'Abuja, Nigeria',
  country: 'Nigeria',
  city: 'Abuja',
  type: 'Residential',
  totalAmount: 1200000,
  fundedAmount: 960000,
  investors: 28,
  status: 'Active',
  statusColor: 'text-secondary bg-secondary/8 border-secondary/20'
},
{
  id: 'proj-6',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_13432a49d-1779312899051.png",
  alt: 'Beachfront resort and hotel development in Mombasa Kenya coastal area',
  name: 'Mombasa Beachfront Resort',
  location: 'Mombasa, Kenya',
  country: 'Kenya',
  city: 'Mombasa',
  type: 'Commercial',
  totalAmount: 3500000,
  fundedAmount: 700000,
  investors: 9,
  status: 'Funding',
  statusColor: 'text-primary bg-primary/8 border-primary/20'
}];


function formatCurrency(amount: number): string {
  if (amount >= 1000000) return `$${(amount / 1000000).toFixed(1)}M`;
  if (amount >= 1000) return `$${(amount / 1000).toFixed(0)}K`;
  return `$${amount}`;
}

const countries = ['All Countries', ...Array.from(new Set(allProjects.map((p) => p.country)))];
const cities = ['All Cities', ...Array.from(new Set(allProjects.map((p) => p.city)))];
const types = ['All Types', ...Array.from(new Set(allProjects.map((p) => p.type)))];
const statuses = ['All Statuses', 'Funding', 'Active', 'Completed'];

export default function MarketplacePage() {
  const [search, setSearch] = useState('');
  const [country, setCountry] = useState('All Countries');
  const [city, setCity] = useState('All Cities');
  const [type, setType] = useState('All Types');
  const [status, setStatus] = useState('All Statuses');
  const [showFilters, setShowFilters] = useState(false);

  const filtered = allProjects.filter((p) => {
    const matchSearch = p.name.toLowerCase().includes(search.toLowerCase()) || p.location.toLowerCase().includes(search.toLowerCase());
    const matchCountry = country === 'All Countries' || p.country === country;
    const matchCity = city === 'All Cities' || p.city === city;
    const matchType = type === 'All Types' || p.type === type;
    const matchStatus = status === 'All Statuses' || p.status === status;
    return matchSearch && matchCountry && matchCity && matchType && matchStatus;
  });

  const hasActiveFilters = country !== 'All Countries' || city !== 'All Cities' || type !== 'All Types' || status !== 'All Statuses';

  const clearFilters = () => {
    setCountry('All Countries');
    setCity('All Cities');
    setType('All Types');
    setStatus('All Statuses');
    setSearch('');
  };

  return (
    <div className="min-h-screen bg-white text-foreground">
      <HomepageNav />
      <main className="pt-16">
        {/* Header */}
        <div className="bg-muted/40 border-b border-border py-12">
          <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
            <p className="text-xs font-semibold tracking-widest uppercase text-primary mb-3">Marketplace</p>
            <h1 className="text-3xl lg:text-4xl font-bold text-foreground mb-3">Explore Verified Projects</h1>
            <p className="text-muted-foreground max-w-xl leading-relaxed">
              Browse escrow-protected real estate projects. Every listing is verified and milestone-tracked.
            </p>
          </div>
        </div>

        <div className="max-w-screen-xl mx-auto px-6 lg:px-10 py-8">
          {/* Search + Filter Bar */}
          <div className="flex flex-col sm:flex-row gap-3 mb-6">
            <div className="relative flex-1">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search projects or locations..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-9 pr-4 py-2.5 rounded-md border border-border bg-white text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary" />
              
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-md border text-sm font-medium transition-colors ${
              showFilters || hasActiveFilters ?
              'border-primary bg-primary/5 text-primary' : 'border-border text-muted-foreground hover:text-foreground hover:border-primary/30'}`
              }>
              
              <SlidersHorizontal size={16} />
              Filters
              {hasActiveFilters &&
              <span className="w-1.5 h-1.5 rounded-full bg-primary" />
              }
            </button>
            {hasActiveFilters &&
            <button
              onClick={clearFilters}
              className="inline-flex items-center gap-1.5 px-3 py-2.5 rounded-md border border-border text-sm text-muted-foreground hover:text-foreground transition-colors">
              
                <X size={14} />
                Clear
              </button>
            }
          </div>

          {/* Filter Panel */}
          {showFilters &&
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6 p-4 bg-muted/40 rounded-xl border border-border">
              {[
            { label: 'Country', value: country, options: countries, setter: setCountry },
            { label: 'City', value: city, options: cities, setter: setCity },
            { label: 'Project Type', value: type, options: types, setter: setType },
            { label: 'Status', value: status, options: statuses, setter: setStatus }].
            map((filter) =>
            <div key={filter.label}>
                  <label className="text-xs font-medium text-muted-foreground mb-1.5 block">{filter.label}</label>
                  <select
                value={filter.value}
                onChange={(e) => filter.setter(e.target.value)}
                className="w-full px-3 py-2 rounded-md border border-border bg-white text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary">
                
                    {filter.options.map((opt) =>
                <option key={opt} value={opt}>{opt}</option>
                )}
                  </select>
                </div>
            )}
            </div>
          }

          {/* Results count */}
          <p className="text-sm text-muted-foreground mb-6">
            Showing <span className="font-semibold text-foreground">{filtered.length}</span> project{filtered.length !== 1 ? 's' : ''}
          </p>

          {/* Project Grid */}
          {filtered.length === 0 ?
          <div className="text-center py-20">
              <p className="text-muted-foreground text-sm">No projects match your filters.</p>
              <button onClick={clearFilters} className="mt-3 text-sm text-primary hover:underline">Clear all filters</button>
            </div> :

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((project) => {
              const progress = Math.round(project.fundedAmount / project.totalAmount * 100);
              return (
                <div
                  key={project.id}
                  className="bg-white rounded-xl border border-border overflow-hidden hover:border-primary/30 hover:shadow-md transition-all duration-200 group">
                  
                    <div className="relative h-44 overflow-hidden bg-muted">
                      <img
                      src={project.image}
                      alt={project.alt}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                    
                      <div className="absolute top-3 left-3">
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${project.statusColor}`}>
                          {project.status === 'Active' && <CheckCircle size={10} className="mr-1" />}
                          {project.status}
                        </span>
                      </div>
                      <div className="absolute top-3 right-3">
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-white/90 text-foreground border border-border">
                          {project.type}
                        </span>
                      </div>
                    </div>

                    <div className="p-5">
                      <h3 className="text-base font-semibold text-foreground mb-1">{project.name}</h3>
                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-4">
                        <MapPin size={12} />
                        {project.location}
                      </div>

                      <div className="mb-4">
                        <div className="flex items-center justify-between text-xs mb-1.5">
                          <span className="text-muted-foreground">Funding Progress</span>
                          <span className="font-semibold text-foreground">{progress}%</span>
                        </div>
                        <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                          <div
                          className="h-full bg-primary rounded-full"
                          style={{ width: `${progress}%` }} />
                        
                        </div>
                      </div>

                      <div className="flex items-center justify-between mb-5">
                        <div>
                          <p className="text-xs text-muted-foreground">Total Amount</p>
                          <p className="text-sm font-bold text-foreground">{formatCurrency(project.totalAmount)}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-muted-foreground">Investors</p>
                          <div className="flex items-center gap-1 justify-end">
                            <Users size={12} className="text-muted-foreground" />
                            <p className="text-sm font-bold text-foreground">{project.investors}</p>
                          </div>
                        </div>
                      </div>

                      <Link
                      href={`/marketplace/${project.id}`}
                      className="flex items-center justify-center gap-2 w-full py-2.5 rounded-md border border-primary text-primary text-sm font-semibold hover:bg-primary hover:text-white transition-all duration-150 active:scale-95">
                      
                        View Details
                        <ArrowRight size={14} />
                      </Link>
                    </div>
                  </div>);

            })}
            </div>
          }
        </div>
      </main>
      <HomepageFooter />
    </div>);

}